// The quick edit, wired (timesheet rules §3): one person's day read from the server (time.day for the
// day, time.history for its last change, the company's non-job codes), drawn by ui-web's QuickEdit from
// quick-edit-model's view, and saved with time.editDay (and time.correct to settle a check as it
// stands). A refusal comes back onto the dialog: someone saving first redraws the day as it is now and
// keeps every change for Save again, said over the fields (QA-774); a time the server refuses goes
// under its field. Opened from the
// board's tech menu (Fix Fri Oct 23…, Edit hours…) and from the Timesheet's and Hours' rows.
import { useEffect, useMemo, useState } from 'react';
import { RpcError } from '@flowology/client';
import { zonedParts } from '@flowology/kernel';
import { QuickEdit, type QuickEditAction, type QuickEditView } from '@flowology/ui-web/timesheet';
import { client } from '../api.ts';
import { useQuery } from '../books/ui.tsx';
import { todayIn } from '../books/model.ts';
import {
  initialQuickEdit,
  quickEditReduce,
  quickEditView,
  refused,
  saveRequests,
  type BookedVisit,
  type QuickEditInput,
  type QuickEditState,
} from './quick-edit-model.ts';
import type { NonJobCode, TimeHistoryItem, TimeSheet } from './timesheet-types.ts';
import { addDays } from '@flowology/kernel';
import { dayWords } from './time-words.ts';

export interface QuickEditOpen {
  staffId: string;
  name: string;
  date: string;
  /** A To fix row's button: that fix chosen. */
  fix?: string;
  pen?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | null;
  /** The board's visits for them that day, at their booked windows. */
  booked?: BookedVisit[];
}

export interface QuickEditDialogProps {
  open: QuickEditOpen;
  timeZone: string;
  onClose: () => void;
  /** After a save: what drew the day reads it again. */
  onSaved?: () => void;
  /** Timesheet ›: the person's Timesheet on that day's period. */
  onTimesheet?: (staffId: string, date: string) => void;
}

const nowMinuteIn = (tz: string) => {
  const p = zonedParts(new Date(), tz);
  return p.hour * 60 + p.minute;
};

export function QuickEditDialog({ open, timeZone, onClose, onSaved, onTimesheet }: QuickEditDialogProps) {
  const today = todayIn(timeZone);
  const [date, setDate] = useState(open.date);
  const ask = { staffId: open.staffId, from: date, to: date };
  const sheet = useQuery<TimeSheet>('time.day', ask);
  const history = useQuery<{ items: TimeHistoryItem[] }>('time.history', { ...ask, limit: 1 });
  const codes = useQuery<{ codes: (NonJobCode & { category: string; active: boolean })[] }>(
    'nonJobCodes.list',
    {},
  );
  const day = sheet.current ? sheet.data?.days.find((d) => d.date === date) : undefined;
  const first = open.name.split(/\s+/)[0] || open.name;
  const input: QuickEditInput | null = useMemo(
    () =>
      day
        ? {
            staffId: open.staffId,
            day,
            today,
            nowMinute: nowMinuteIn(timeZone),
            person: { name: open.name, first, pen: open.pen ?? null },
            booked: date === open.date ? (open.booked ?? []) : [],
            codes: (codes.data?.codes ?? []).filter((c) => c.category === 'activity'),
            period: sheet.data?.periods.find((p) => p.startDate <= date && date <= p.endDate) ?? null,
            timeZone,
            lastChange: history.data?.items[0] ?? null,
          }
        : null,
    [day, codes.data, history.data, sheet.data, date, open, timeZone, today, first],
  );
  // a new day starts from its likeliest fix; the same day read again (after someone saved first)
  // keeps what was typed
  const [state, setState] = useState<{ date: string; s: QuickEditState } | null>(null);
  useEffect(() => {
    if (input && state?.date !== date)
      setState({ date, s: initialQuickEdit(input, date === open.date && open.fix ? { fix: open.fix } : {}) });
  }, [input, date, state?.date, open.date, open.fix]);

  const s = state?.date === date ? state.s : null;
  const view: QuickEditView =
    input && s
      ? quickEditView(input, s)
      : waiting(open.name, date, sheet.error ? `${dayWords(date)} didn’t load: ${sheet.error}` : null);

  const act = (a: QuickEditAction) => {
    if (a.type === 'step') {
      // ‹ › stops at today; with a change typed, Cancel asks first (the footer's question)
      const next = addDays(date, a.dir);
      if (s?.dirty || next > today) return;
      setDate(next);
      return;
    }
    if (a.type === 'timesheet') {
      onTimesheet?.(open.staffId, date);
      return;
    }
    if (input && s) setState({ date, s: quickEditReduce(input, s, a) });
  };

  const submit = async () => {
    if (!input || !s) return;
    try {
      for (const req of saveRequests(input, s)) await client.call(req.action, req.input);
    } catch (e) {
      if (!(e instanceof RpcError)) throw e;
      const r = refused(input, s, {
        code: e.code,
        message: e.message,
        detail: (e.detail ?? undefined) as Parameters<typeof refused>[2]['detail'],
      });
      setState({ date, s: r.state });
      // someone saved first: the day as it is now (and its last change, theirs), under the changes kept
      if (r.state.conflict) {
        void sheet.reload();
        void history.reload();
      }
      throw new Error(r.message, { cause: e });
    }
    onSaved?.();
  };

  return (
    <QuickEdit
      view={{ ...view, steps: { back: !s?.dirty, forward: !s?.dirty && date < today } }}
      isOpen
      onClose={onClose}
      onAction={act}
      onSubmit={submit}
    />
  );
}

/** While the day loads (or if it didn't): the name and the day, and Close. */
function waiting(name: string, date: string, failed: string | null): QuickEditView {
  return {
    title: `${name} · ${dayWords(date)}`,
    sub: '',
    steps: { back: false, forward: false },
    strip: { booked: [], clocked: [], ghosts: [], nowMinute: null, midnight: null, pressable: false },
    notes: [],
    body: { kind: 'empty', words: failed ?? `Loading ${dayWords(date)}…` },
    fix: null,
    change: null,
    add: null,
    why: null,
    ask: null,
    pending: null,
    conflict: null,
    footer: { note: '', canSave: false, submitLabel: 'Save' },
    discardText: '',
    isDirty: false,
  };
}
